import React, { useEffect, useState } from "react";
import { Edit2, Save, X, Bell, Lock, User, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import AWS from "aws-sdk";
import { useSelector } from "react-redux";
import { IconButton, InputAdornment, TextField } from "@mui/material";

const RadioCheck = ({ isSelected, onClick, disabled = false, children }) => (
  <div className="flex items-center gap-3">
    <div 
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ease-in-out
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isSelected ? 'bg-[#003366] border-[#003366] hover:bg-[#004488]' : 'bg-white border-gray-300 hover:border-gray-400'}`}
      onClick={!disabled ? onClick : undefined}
    >
      {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);
const ProfileSettings = () => {
  const [notificationSetting, setNotificationSetting] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [profile, setProfile] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailVerified: "",
    phoneNumberVerified: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const region = process.env.REACT_APP_REGION;
  let accessToken = useSelector((state) => state.accessToken.value);
  const cognito = new AWS.CognitoIdentityServiceProvider({ region: region });

  useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => {
        setSaveError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveError]);

  useEffect(() => {
    fetchUserProfile();
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    const params = { AccessToken: accessToken };
    try {
      const response = await cognito.getUser(params).promise();
      const notificationAttr = response.UserAttributes.find(
        attr => attr.Name === 'custom:notificationSetting'
      );
      if (notificationAttr) {
        setNotificationSetting(notificationAttr.Value);
      }
    } catch (error) {
      console.error("Error fetching notification settings:", error);
      setSaveError("Failed to fetch notification settings");
    }
  };

  const fetchUserProfile = async () => {
    const params = { AccessToken: accessToken };
    try {
      const response = await cognito.getUser(params).promise();
      const attributes = response.UserAttributes.reduce((acc, attr) => {
        acc[attr.Name] = attr.Value;
        return acc;
      }, {});
      setProfile({
        email: attributes.email,
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        phoneNumber: attributes.phone_number,
        emailVerified: attributes.email_verified,
        phoneNumberVerified: attributes.phone_number_verified,
      });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setSaveError("Failed to fetch user profile");
    }
  };

  const handleUpdateProfile = async () => {
    setIsSavingProfile(true);
    const attributes = [
      { Name: "given_name", Value: profile.firstName },
      { Name: "family_name", Value: profile.lastName },
      { Name: "phone_number", Value: profile.phoneNumber },
    ];

    try {
      await cognito
        .updateUserAttributes({
          AccessToken: accessToken,
          UserAttributes: attributes,
        })
        .promise();
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setSaveError("Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveNotification = async () => {
    setIsSavingNotification(true);
    try {
      await cognito
        .updateUserAttributes({
          AccessToken: accessToken,
          UserAttributes: [
            {
              Name: "custom:notificationSetting",
              Value: notificationSetting,
            },
          ],
        })
        .promise();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating notification settings:", error);
      setSaveError("Failed to update notification settings");
    } finally {
      setIsSavingNotification(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSaveError("New passwords do not match");
      return;
    }

    setIsSavingPassword(true);
    try {
      await cognito
        .changePassword({
          AccessToken: accessToken,
          PreviousPassword: passwordForm.currentPassword,
          ProposedPassword: passwordForm.newPassword,
        })
        .promise();

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error changing password:", error);
      setSaveError("Failed to change password");
    } finally {
      setIsSavingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-[#003366] mb-6">Profile Settings</h1>

      {saveSuccess && (
        <div className="mb-4 p-4 bg-green-50 text-green-800 border border-green-200 rounded-lg">
          Changes saved successfully!
        </div>
      )}

      {saveError && (
        <div className="mb-4 p-4 bg-red-50 text-red-800 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
            {saveError}
          </div>
          <button onClick={() => setSaveError("")}>
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-[#003366]" />
              <h2 className="text-xl font-semibold">Profile</h2>
            </div>
            <button
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                isEditing
                  ? "bg-[#FFD700] text-[#003366] hover:bg-[#FFD700]/80"
                  : "bg-[#003366] text-white hover:bg-[#004488]"
              }`}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </>
              )}
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <input
                type="text"
                value={profile.firstName}
                disabled={!isEditing}
                onChange={(e) =>
                  setProfile({ ...profile, firstName: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <input
                type="text"
                value={profile.lastName}
                disabled={!isEditing}
                onChange={(e) =>
                  setProfile({ ...profile, lastName: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                value={profile.phoneNumber}
                disabled={!isEditing}
                onChange={(e) =>
                  setProfile({ ...profile, phoneNumber: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {isEditing && (
              <button
                className="w-full flex items-center justify-center px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] mt-4 disabled:opacity-50"
                onClick={handleUpdateProfile}
                disabled={isSavingProfile}
              >
                {isSavingProfile ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Notifications Section */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Bell className="h-5 w-5 text-[#003366]" />
            <h2 className="text-xl font-semibold">Notifications</h2>
          </div>

          <div className="space-y-6">
            <RadioCheck
              isSelected={notificationSetting === "all"}
              onClick={() => setNotificationSetting("all")}
              disabled={isSavingNotification}
            >
              <div className="text-sm">
                <div className="font-medium">All Notifications</div>
                <p className="text-gray-500">Receive notifications for all environments</p>
              </div>
            </RadioCheck>

            <RadioCheck
              isSelected={notificationSetting === "prod"}
              onClick={() => setNotificationSetting("prod")}
              disabled={isSavingNotification}
            >
              <div className="text-sm">
                <div className="font-medium">Production Only</div>
                <p className="text-gray-500">Notifications for production environment only</p>
              </div>
            </RadioCheck>

            <RadioCheck
              isSelected={notificationSetting === "none"}
              onClick={() => setNotificationSetting("none")}
              disabled={isSavingNotification}
            >
              <div className="text-sm">
                <div className="font-medium">No Notifications</div>
                <p className="text-gray-500">Turn off all notifications</p>
              </div>
            </RadioCheck>
          </div>

          <button
            className="w-full flex items-center justify-center px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] mt-6 disabled:opacity-50"
            onClick={handleSaveNotification}
            disabled={isSavingNotification}
          >
            {isSavingNotification ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Preferences
              </>
            )}
          </button>
        </div>


        {/* Password Section */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="h-5 w-5 text-[#003366]" />
            <h2 className="text-xl font-semibold">Change Password</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <TextField
                id="currentPassword"
                type={showPassword.currentPassword ? "text" : "password"}
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    currentPassword: e.target.value,
                  })
                }
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock className="h-6 w-6 text-[#003366]" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => togglePasswordVisibility("currentPassword")}
                        edge="end"
                        size="small"
                      >
                        {showPassword.currentPassword ? (
                          <EyeOff className="h-4 w-4 text-[#003366]" />
                        ) : (
                          <Eye className="h-4 w-4 text-[#003366]" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <TextField
                id="newPassword"
                type={showPassword.newPassword ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      newPassword: e.target.value,
                    })
                  }
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("newPassword")}
                          edge="end"
                          size="small"
                        >
                          {showPassword.newPassword ? (
                            <EyeOff className="h-4 w-4 text-[#003366]" />
                          ) : (
                            <Eye className="h-4 w-4 text-[#003366]" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <TextField
                  id="confirmPassword"
                  type={showPassword.confirmPassword ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirmPassword: e.target.value,
                    })
                  }
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("confirmPassword")}
                          edge="end"
                          size="small"
                        >
                          {showPassword.confirmPassword ? (
                            <EyeOff className="h-4 w-4 text-[#003366]" />
                          ) : (
                            <Eye className="h-4 w-4 text-[#003366]" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
  
              <button
                className="w-full flex items-center justify-center px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] disabled:opacity-50"
                onClick={handleChangePassword}
                disabled={isSavingPassword}
              >
                {isSavingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Update Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
  
      </div>
    );
  };
  
  export default ProfileSettings;